import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TenorGif } from './TenorGif';
import { URL_REGEX, classifyUrl } from '../mediaUrls';

// Rendu du contenu d'un message : detecte les URLs et affiche
// image / gif Tenor / audio, sinon du texte brut.
// La detection est dans ../mediaUrls (module pur, teste).

// Lecteur audio compact et fiable (PC + mobile). Auto-play quand c'est le
// dernier message revele ; sinon en pause avec un bouton pour reecouter.
function AudioClip({ src, autoPlay }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (autoPlay) {
      try { el.currentTime = 0; } catch { /* ignore */ }
      const p = el.play();
      if (p && p.then) p.then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      try { el.pause(); } catch { /* ignore */ }
      setPlaying(false);
    }
  }, [autoPlay, src]);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) {
      const p = el.play();
      if (p && p.then) p.then(() => setPlaying(true)).catch(() => {});
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px',
      background: '#f3f4f6', border: '2px solid #e5e7eb', boxShadow: '0 3px 0 #d1d5db',
      margin: '4px 0', maxWidth: 320,
    }}>
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Lire'}
        disabled={failed}
        style={{
          width: 30, height: 30, borderRadius: '50%', border: 'none',
          background: failed ? '#9ca3af' : 'var(--color-primary, #5663f7)', color: '#fff',
          cursor: failed ? 'default' : 'pointer', fontSize: 13, lineHeight: 1,
        }}
      >{playing ? '⏸' : '▶'}</button>
      <span style={{ fontSize: '0.8rem', color: '#374151' }}>
        {failed ? 'audio indisponible' : (playing ? 'lecture...' : 'extrait audio')}
      </span>
      <audio
        ref={ref}
        src={src}
        preload="metadata"
        onEnded={() => setPlaying(false)}
        onError={() => setFailed(true)}
        style={{ display: 'none' }}
      />
    </span>
  );
}

// Image avec zoom au survol (apercu plein ecran). Clic pour mobile.
function ZoomImage({ src, className }) {
  const [zoom, setZoom] = useState(false);
  return (
    <>
      <img
        src={src}
        alt=""
        className={className}
        loading="lazy"
        style={{ cursor: 'zoom-in' }}
        onMouseEnter={() => setZoom(true)}
        onMouseLeave={() => setZoom(false)}
        onClick={() => setZoom(true)}
      />
      {zoom && createPortal(
        <div
          onClick={() => setZoom(false)}
          style={{
            position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)', zIndex: 9999, cursor: 'zoom-out',
          }}
        >
          <img
            src={src}
            alt=""
            style={{ maxWidth: '85vw', maxHeight: '85vh', objectFit: 'contain', border: '4px solid #fff', boxShadow: '0 10px 40px rgba(0,0,0,0.6)' }}
          />
        </div>,
        document.body
      )}
    </>
  );
}

// Lecteur video (mp4/mov/webm/mkv) : controles natifs, pas d'autoplay.
function VideoClip({ src, className }) {
  return (
    <video
      src={src}
      className={className}
      controls
      playsInline
      preload="metadata"
      style={{ display: 'block', maxWidth: 320, margin: '4px 0', borderRadius: 4 }}
    />
  );
}

export function MessageBody({ content, imgClassName = 'chat-img', autoPlayAudio = false }) {
  return (
    <>
      {(content || '').split(URL_REGEX).map((part, idx) => {
        if (!part) return null;
        switch (classifyUrl(part)) {
          case 'tenor': return <TenorGif key={idx} url={part} height={220} />;
          case 'image': return <ZoomImage key={idx} src={part} className={imgClassName} />;
          case 'video': return <VideoClip key={idx} src={part} className={imgClassName} />;
          case 'audio': return <AudioClip key={idx} src={part} autoPlay={autoPlayAudio} />;
          default: return <span key={idx}>{part}</span>;
        }
      })}
    </>
  );
}
